/**
 * Reveals The Solution's product shot as it scrolls into view — one rise-and-fade,
 * the first time the image enters the viewport.
 *
 * Plain HTMLElement rather than Horizon's Component base, matching
 * reviews-wall.js and ingredient-scroll.js: this needs no shadow DOM, refs or
 * declarative event binding, just an IntersectionObserver.
 *
 * All the actual motion lives in the section's stylesheet. This file only flips
 * `data-reveal` to "in"; it never touches style properties directly, so the
 * timing, distance and reduced-motion handling all stay in one place in CSS.
 *
 * Why the hidden starting state is keyed off `:defined` in that stylesheet and
 * not applied here: if this file fails to load, the custom element is never
 * upgraded, `:not(:defined)` still matches, and the image simply renders
 * normally. Hiding the image from JS would instead mean a broken script leaves
 * it permanently invisible.
 */
class SolutionSpotlightMedia extends HTMLElement {
  connectedCallback() {
    // No IntersectionObserver (or the element re-connected after already
    // revealing): show it outright rather than leaving it hidden.
    if (!('IntersectionObserver' in window) || this.dataset.reveal === 'in') {
      this.dataset.reveal = 'in';
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.dataset.reveal = 'in';
          // One-shot: this is a reveal, not a scrubbed effect, so it must not
          // replay or reverse when the section is scrolled back past.
          this.disconnectObserver();
        }
      },
      {
        // Wait until the image is ~15% of the viewport past the bottom edge
        // before starting, so the animation is actually watched rather than
        // finishing off-screen the instant the first pixel appears.
        rootMargin: '0px 0px -15% 0px',
        threshold: 0,
      }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.disconnectObserver();
  }

  disconnectObserver() {
    this.observer?.disconnect();
    this.observer = null;
  }
}

if (!customElements.get('solution-spotlight-media')) {
  customElements.define('solution-spotlight-media', SolutionSpotlightMedia);
}
